// src/store/authStore.js
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { auth, db } from "config/firebaseConfig";
import { formatTimestamp } from "utils/dateUtilis";

/**
 * Only a lightweight shape is persisted (userId + isAuthenticated).
 * Full user object is kept in-memory (not persisted) to avoid stale data issues.
 */
const formatUserData = (firestoreData = {}, firebaseUser = {}, id) => ({
  id,
  auth: {
    uid: firebaseUser?.uid,
    email: firebaseUser?.email,
    emailVerified: firebaseUser?.emailVerified || false,
    phoneNumber: firebaseUser?.phoneNumber,
    displayName: firebaseUser?.displayName,
    photoURL: firebaseUser?.photoURL,
    isAnonymous: firebaseUser?.isAnonymous || false,
    metadata: {
      creationTime: firebaseUser?.metadata?.creationTime,
      lastSignInTime: firebaseUser?.metadata?.lastSignInTime,
    },
    providerData: firebaseUser?.providerData || [],
    tenantId: firebaseUser?.tenantId,
    providerId: firebaseUser?.providerId,
  },
  ...firestoreData,
  role: firestoreData?.role === "individual" ? "customer" : firestoreData?.role,
  dateCreated: firestoreData?.dateCreated
    ? formatTimestamp(firestoreData.dateCreated)
    : null,
  KYC_Completed_Date: firestoreData?.KYC_Completed_Date
    ? formatTimestamp(firestoreData.KYC_Completed_Date)
    : null,
  lastUpdated: firestoreData?.lastUpdated
    ? formatTimestamp(firestoreData.lastUpdated)
    : null,
  dateOfBirth: firestoreData?.dateOfBirth
    ? formatTimestamp(firestoreData.dateOfBirth)
    : null,
  currentLocation: {
    ...firestoreData?.currentLocation,
    timestamp: formatTimestamp(firestoreData?.currentLocation?.timestamp),
  },
  lastWithdrawal: firestoreData?.lastWithdrawal
    ? formatTimestamp(firestoreData.lastWithdrawal)
    : null,
});

export const useAuthStore = create(
  persist(
    (set, get) => ({
      /* ---------------------------
         State (in-memory)
         --------------------------- */
      user: null, // full user object — NOT persisted
      loading: true,
      error: null,
      isAuthenticated: false, // persisted
      userId: null, // persisted (minimal)
      hasCompletedKYC: false,

      // Unsubscribe functions (not persisted)
      unsubscribeAuth: null,
      unsubscribeSnapshot: null,

      /* ---------------------------
         Actions
         --------------------------- */
      setUser: (user) => {
        set({ user, loading: false, error: null, isAuthenticated: true });
      },

      setLoading: (loading) => set({ loading }),

      setError: (error) => set({ error, loading: false }),

      setIsAuthenticated: (isAuth) => set({ isAuthenticated: isAuth }),

      setUserId: (id) => set({ userId: id }),

      setKYCStatus: (status) => set({ hasCompletedKYC: status }),

      clearAuthInMemory: () =>
        set({
          user: null,
          loading: false,
          error: null,
          hasCompletedKYC: false,
        }),

      /* ---------------------------
         Cleanup helper (safe)
         --------------------------- */
      _clearUnsubscribes: () => {
        const state = get();
        try {
          if (state.unsubscribeSnapshot) {
            state.unsubscribeSnapshot();
          }
        } catch (e) {
          console.warn("Error unsubscribing snapshot:", e);
        }
        try {
          if (state.unsubscribeAuth) {
            state.unsubscribeAuth();
          }
        } catch (e) {
          console.warn("Error unsubscribing auth:", e);
        }
        // remove references
        set({ unsubscribeSnapshot: null, unsubscribeAuth: null });
      },

      /* ---------------------------
         Public cleanup & signOut
         --------------------------- */
      cleanup: () => {
        get()._clearUnsubscribes();
        get().clearAuthInMemory();
        // keep persisted userId/isAuthenticated depending on your policy.
        // we will reset persisted state on manual signOut below.
      },

      signOut: async () => {
        // optional: call firebase signOut here if needed (import signOut from firebase/auth)
        get()._clearUnsubscribes();
        set({
          user: null,
          isAuthenticated: false,
          userId: null,
          loading: false,
          error: null,
          hasCompletedKYC: false,
        });
      },

      /* ---------------------------
         Core: initializeAuthListener
         - returns a stable cleanup function
         - persists only userId + isAuthenticated (via partialize)
         --------------------------- */
      initializeAuthListener: () => {
        // if already have an auth unsubscribe, return a cleanup that uses existing unsubscribe
        if (get().unsubscribeAuth) {
          // return a stable cleanup that uses the current unsubscribes
          return () => {
            get()._clearUnsubscribes();
          };
        }

        // local references captured so returned cleanup uses the correct unsub functions
        let localUnsubscribeSnapshot = null;
        let localUnsubscribeAuth = null;

        const setupUserSnapshot = (uid, role, firebaseUser) => {
          const collectionName = role === "admin" ? "starrik-admin" : "users";
          const userDocRef = doc(db, collectionName, uid);

          const unsubscribe = onSnapshot(
            userDocRef,
            (docSnapshot) => {
              if (!docSnapshot.exists()) {
                // if the doc disappears, clear in-memory user but keep persisted id
                set({ user: null, loading: false });
                return;
              }
              const firestoreData = docSnapshot.data();
              const formatted = formatUserData(
                firestoreData,
                firebaseUser,
                docSnapshot.id
              );

              // update in-memory user and KYC status
              set({
                user: formatted,
                isAuthenticated: true,
                loading: false,
                hasCompletedKYC: !!formatted.hasCompletedKYC,
              });
            },
            (err) => {
              console.error("onSnapshot error:", err);
              set({ error: err.message, loading: false });
            }
          );

          // assign to both local & store reference
          localUnsubscribeSnapshot = unsubscribe;
          set({ unsubscribeSnapshot: unsubscribe });
          return unsubscribe;
        };

        // firebase auth state listener
        const handleAuthChange = async (firebaseUser) => {
          
          try {
            console.log(firebaseUser);

            if (!firebaseUser) {
              // user signed out
              // Clear in-memory but keep persisted fields minimal (we'll set them to null here)
              set({
                user: null,
                isAuthenticated: false,
                userId: null,
                loading: false,
              });

              // clear snapshot if exists
              if (localUnsubscribeSnapshot) {
                localUnsubscribeSnapshot();
                localUnsubscribeSnapshot = null;
                set({ unsubscribeSnapshot: null });
              }
              return;
            }

            // user signed in — set minimal persisted fields immediately
            set({
              loading: true,
              userId: firebaseUser.uid,
              isAuthenticated: true,
            });

            // check if starrik collection user exists first
            const starrikDoc = await getDoc(
              doc(db, "starrik-admin", firebaseUser.uid)
            );
            if (starrikDoc.exists()) {
              setupUserSnapshot(firebaseUser.uid, "admin", firebaseUser);
              return;
            }

            // fallback to users collection
            const userDoc = await getDoc(doc(db, "users", firebaseUser.uid));
            if (!userDoc.exists()) {
              throw new Error("User account not found");
            }

            const firestoreUserData = userDoc.data();
            // setup real-time snapshot to keep user updated
            setupUserSnapshot(
              firebaseUser.uid,
              firestoreUserData.role,
              firebaseUser
            );
          } catch (err) {
            console.error("Auth listener error:", err);
            set({ error: err?.message || String(err), loading: false });
          }
        };

        // subscribe to firebase auth state changes
        localUnsubscribeAuth = onAuthStateChanged(auth, handleAuthChange);

        // save to store for other callers if needed
        set({ unsubscribeAuth: localUnsubscribeAuth });

        // Return a cleanup function that closes local unsubscribes and clears store refs
        const cleanup = () => {
          try {
            if (localUnsubscribeSnapshot) {
              localUnsubscribeSnapshot();
            }
          } catch (e) {
            console.warn("cleanup snapshot error", e);
          }
          try {
            if (localUnsubscribeAuth) {
              localUnsubscribeAuth();
            }
          } catch (e) {
            console.warn("cleanup auth error", e);
          }
          // clear store references
          set({ unsubscribeSnapshot: null, unsubscribeAuth: null });
          // leave persisted (userId/isAuthenticated) as-is or clear depending on your policy;
          // do not set heavy 'user' here, that is handled by snapshot listeners
        };

        return cleanup;
      },
    }),
    {
      name: "auth-storage",
      version: 1,
      /**
       * Persist only minimal serializable data: userId and isAuthenticated.
       * DO NOT persist the full user object or unsubscribe functions.
       */
      partialize: (state) => ({
        userId: state.userId,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

/* ---------------------------
   Convenient selectors & hook
   - useAuthStore: direct store access with selectors (recommended)
   - specialized hooks avoid subscribing to the whole store
   --------------------------- */
export const useAuth = () => {
  // subscribe only to the needed fields to avoid unnecessary re-renders
  const user = useAuthStore((s) => s.user);
  const loading = useAuthStore((s) => s.loading);
  const error = useAuthStore((s) => s.error);
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const userId = useAuthStore((s) => s.userId);
  const hasCompletedKYC = useAuthStore((s) => s.hasCompletedKYC);
  const setUser = useAuthStore((s) => s.setUser);
  const setIsAuthenticated = useAuthStore((s) => s.setIsAuthenticated);

  // computed
  const role = user?.role ?? null;

  return {
    user,
    loading,
    error,
    isAuthenticated,
    userId,
    hasCompletedKYC,
    role,
    // actions
    setUser,
    setIsAuthenticated,
    initializeAuthListener: useAuthStore((s) => s.initializeAuthListener),
    cleanup: useAuthStore((s) => s.cleanup),
    signOut: useAuthStore((s) => s.signOut),
  };
};

/* tiny helper selectors to be used in components where you need minimal reactivity */
export const useUser = () => useAuthStore((s) => s.user);
export const useIsAuthenticated = () => useAuthStore((s) => s.isAuthenticated);
export const useUserId = () => useAuthStore((s) => s.userId);
export const useAuthLoading = () => useAuthStore((s) => s.loading);
export default useAuthStore;
